// MJOS-132 — three.js CC0 character lab (multi-character).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { HeadTTS } from "@met4citizen/headtts";
import { Loader2, Volume2, RotateCcw } from "lucide-react";

const THREE_BASE = "https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/models/gltf";
// Quaternius CC0 animals via the catch-the-animal demo repo (Husky/Shiba/Wolf/Fox).
const QUATERNIUS_BASE = "https://raw.githubusercontent.com/benjaminpjones/catch-the-animal/main/models/glTF";

type Character = {
  id: string;
  label: string;
  emoji: string;
  url: string;
  hasMorphs: boolean;
  /** Target normalized height. All characters scaled to this so they frame
   *  consistently regardless of native units. */
  normalizedHeight?: number;
};

const CHARACTERS: Character[] = [
  {
    id: "robot",
    label: "Robot",
    emoji: "🤖",
    url: `${THREE_BASE}/RobotExpressive/RobotExpressive.glb`,
    hasMorphs: true,
    normalizedHeight: 2,
  },
  {
    id: "soldier",
    label: "Soldier",
    emoji: "🪖",
    url: `${THREE_BASE}/Soldier.glb`,
    hasMorphs: false,
    normalizedHeight: 2,
  },
  {
    id: "horse",
    label: "Horse",
    emoji: "🐴",
    url: `${THREE_BASE}/Horse.glb`,
    hasMorphs: false,
    normalizedHeight: 2,
  },
  {
    id: "husky",
    label: "Husky",
    emoji: "🐕",
    url: `${QUATERNIUS_BASE}/Husky.gltf`,
    hasMorphs: false,
    normalizedHeight: 1.4,
  },
  {
    id: "shiba",
    label: "Shiba Inu",
    emoji: "🐶",
    url: `${QUATERNIUS_BASE}/ShibaInu.gltf`,
    hasMorphs: false,
    normalizedHeight: 1.2,
  },
  {
    id: "wolf",
    label: "Wolf",
    emoji: "🐺",
    url: `${QUATERNIUS_BASE}/Wolf.gltf`,
    hasMorphs: false,
    normalizedHeight: 1.5,
  },
  {
    id: "fox",
    label: "Fox",
    emoji: "🦊",
    url: `${QUATERNIUS_BASE}/Fox.gltf`,
    hasMorphs: false,
    normalizedHeight: 1.3,
  },
  {
    id: "flamingo",
    label: "Flamingo",
    emoji: "🦩",
    url: `${THREE_BASE}/Flamingo.glb`,
    hasMorphs: false,
    normalizedHeight: 2,
  },
  {
    id: "parrot",
    label: "Parrot",
    emoji: "🦜",
    url: `${THREE_BASE}/Parrot.glb`,
    hasMorphs: false,
    normalizedHeight: 1.4,
  },
  {
    id: "stork",
    label: "Stork",
    emoji: "🦢",
    url: `${THREE_BASE}/Stork.glb`,
    hasMorphs: false,
    normalizedHeight: 1.8,
  },
];

const HEADTTS_WORKER =
  "https://cdn.jsdelivr.net/npm/@met4citizen/headtts@1.3/modules/worker-tts.mjs";
const HEADTTS_DICT =
  "https://cdn.jsdelivr.net/npm/@met4citizen/headtts@1.3/dictionaries/";

const TEST_SCRIPT =
  "Hi, I'm a CC zero character. My mouth tracks Kokoro audio amplitude via a face morph target.";

const MORPH_ANGRY = 0;
const MORPH_SURPRISED = 1;
const MORPH_SAD = 2;
type ExpressionName = "Angry" | "Surprised" | "Sad";
const EXPRESSIONS: { name: ExpressionName; index: number }[] = [
  { name: "Angry", index: MORPH_ANGRY },
  { name: "Surprised", index: MORPH_SURPRISED },
  { name: "Sad", index: MORPH_SAD },
];

export function RobotPanel() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const animFrameRef = useRef<number>(0);

  const currentSceneObjectRef = useRef<THREE.Object3D | null>(null);
  const headMeshRef = useRef<THREE.Mesh | null>(null);
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const clipsRef = useRef<THREE.AnimationClip[]>([]);
  const activeActionRef = useRef<THREE.AnimationAction | null>(null);

  const ttsRef = useRef<HeadTTS | null>(null);
  const mouthAmpRef = useRef<number>(0);
  const expressionToggleRef = useRef<Record<ExpressionName, boolean>>({
    Angry: false,
    Surprised: false,
    Sad: false,
  });

  const [activeCharacterId, setActiveCharacterId] = useState<string>("robot");
  const activeCharacter = useMemo(
    () => CHARACTERS.find((c) => c.id === activeCharacterId) || CHARACTERS[0],
    [activeCharacterId],
  );

  const [loadState, setLoadState] = useState<"loading" | "loaded" | "error">("loading");
  const [loadPct, setLoadPct] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [availableClipNames, setAvailableClipNames] = useState<string[]>([]);
  const [activeClipName, setActiveClipName] = useState<string | null>(null);

  const [ttsState, setTtsState] = useState<"cold" | "warming" | "ready" | "speaking" | "error">("cold");
  const [ttsError, setTtsError] = useState<string | null>(null);
  const [activeExpressions, setActiveExpressions] = useState<Record<ExpressionName, boolean>>({
    Angry: false,
    Surprised: false,
    Sad: false,
  });

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1c1917);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      35,
      node.clientWidth / node.clientHeight,
      0.1,
      200,
    );
    camera.position.set(0, 1.5, 4);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(node.clientWidth, node.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    node.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 1, 0);
    controls.minDistance = 0.5;
    controls.maxDistance = 30;
    controls.enablePan = true;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.update();
    controlsRef.current = controls;

    const hemi = new THREE.HemisphereLight(0xffffff, 0x8d8d8d, 3);
    hemi.position.set(0, 20, 0);
    scene.add(hemi);
    const dir = new THREE.DirectionalLight(0xffffff, 3);
    dir.position.set(0, 20, 10);
    scene.add(dir);

    const clock = new THREE.Clock();
    const tick = () => {
      animFrameRef.current = requestAnimationFrame(tick);
      const dt = clock.getDelta();
      if (mixerRef.current) mixerRef.current.update(dt);
      if (controlsRef.current) controlsRef.current.update();

      const mesh = headMeshRef.current;
      if (mesh?.morphTargetInfluences) {
        const audioAmp = mouthAmpRef.current;
        mouthAmpRef.current = Math.max(0, mouthAmpRef.current - dt * 2.0);
        const t = expressionToggleRef.current;
        mesh.morphTargetInfluences[MORPH_ANGRY] = t.Angry ? 1 : 0;
        mesh.morphTargetInfluences[MORPH_SAD] = t.Sad ? 1 : 0;
        mesh.morphTargetInfluences[MORPH_SURPRISED] = t.Surprised ? 1 : audioAmp;
      }

      renderer.render(scene, camera);
    };
    tick();

    const onResize = () => {
      if (!rendererRef.current || !cameraRef.current) return;
      cameraRef.current.aspect = node.clientWidth / node.clientHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(node.clientWidth, node.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", onResize);
      controls.dispose();
      renderer.dispose();
      if (node.contains(renderer.domElement)) {
        node.removeChild(renderer.domElement);
      }
      sceneRef.current = null;
      rendererRef.current = null;
      cameraRef.current = null;
      controlsRef.current = null;
    };
  }, []);

  const loadCharacter = useCallback(async (character: Character) => {
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!scene || !camera || !controls) return;

    if (currentSceneObjectRef.current) {
      scene.remove(currentSceneObjectRef.current);
      currentSceneObjectRef.current = null;
    }
    if (mixerRef.current) {
      mixerRef.current.stopAllAction();
      mixerRef.current = null;
    }
    headMeshRef.current = null;
    clipsRef.current = [];
    activeActionRef.current = null;
    setActiveClipName(null);
    setAvailableClipNames([]);

    setLoadState("loading");
    setLoadPct(0);
    setLoadError(null);

    const loader = new GLTFLoader();
    try {
      const gltf = await new Promise<{
        scene: THREE.Group;
        animations: THREE.AnimationClip[];
      }>((resolve, reject) => {
        loader.load(
          character.url,
          (g) => resolve(g),
          (ev) => {
            if (ev.lengthComputable) {
              setLoadPct(Math.min(100, Math.round((ev.loaded / ev.total) * 100)));
            }
          },
          (err) => reject(err),
        );
      });

      gltf.scene.updateMatrixWorld(true);

      const rawBbox = new THREE.Box3().setFromObject(gltf.scene);
      const rawSize = rawBbox.getSize(new THREE.Vector3());
      const rawHeight = Math.max(rawSize.y, 0.001);
      const targetH = character.normalizedHeight ?? 2;
      const scale = targetH / rawHeight;
      gltf.scene.scale.setScalar(scale);
      gltf.scene.updateMatrixWorld(true);

      const scaledBbox = new THREE.Box3().setFromObject(gltf.scene);
      const scaledCenter = scaledBbox.getCenter(new THREE.Vector3());
      gltf.scene.position.x -= scaledCenter.x;
      gltf.scene.position.z -= scaledCenter.z;
      gltf.scene.position.y -= scaledBbox.min.y;
      gltf.scene.updateMatrixWorld(true);

      scene.add(gltf.scene);
      currentSceneObjectRef.current = gltf.scene;

      gltf.scene.traverse((obj) => {
        if ((obj as THREE.Mesh).isMesh) {
          const mesh = obj as THREE.Mesh;
          if (mesh.morphTargetDictionary || mesh.morphTargetInfluences) {
            headMeshRef.current = mesh;
          }
        }
      });

      const finalBbox = new THREE.Box3().setFromObject(gltf.scene);
      const finalSize = finalBbox.getSize(new THREE.Vector3());
      const finalCenter = finalBbox.getCenter(new THREE.Vector3());
      const maxDim = Math.max(finalSize.x, finalSize.y, finalSize.z);
      const fovRad = (camera.fov * Math.PI) / 180;
      const dist = (maxDim / 2) / Math.tan(fovRad / 2) * 1.8;
      camera.position.set(finalCenter.x, finalCenter.y + finalSize.y * 0.05, finalCenter.z + dist);
      camera.lookAt(finalCenter.x, finalCenter.y, finalCenter.z);
      controls.target.set(finalCenter.x, finalCenter.y, finalCenter.z);
      controls.minDistance = dist * 0.2;
      controls.maxDistance = dist * 5;
      controls.update();

      const mixer = new THREE.AnimationMixer(gltf.scene);
      mixerRef.current = mixer;
      clipsRef.current = gltf.animations;
      const clipNames = gltf.animations.map((c) => c.name).filter((n) => n);
      setAvailableClipNames(clipNames);

      // Prefer "Idle" or "Walk" as starting animation if available, else first.
      const preferred = gltf.animations.find((c) => c.name === "Idle") ||
        gltf.animations.find((c) => c.name === "Walk") ||
        gltf.animations[0];
      if (preferred) {
        const action = mixer.clipAction(preferred);
        action.play();
        activeActionRef.current = action;
        setActiveClipName(preferred.name);
      }

      setLoadState("loaded");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setLoadError(msg);
      setLoadState("error");
    }
  }, []);

  useEffect(() => {
    loadCharacter(activeCharacter);
  }, [activeCharacter, loadCharacter]);

  const switchCharacter = useCallback((id: string) => {
    expressionToggleRef.current = { Angry: false, Surprised: false, Sad: false };
    setActiveExpressions({ Angry: false, Surprised: false, Sad: false });
    mouthAmpRef.current = 0;
    setActiveCharacterId(id);
  }, []);

  const playClip = useCallback((name: string) => {
    const mixer = mixerRef.current;
    if (!mixer) return;
    const clip = clipsRef.current.find((c) => c.name === name);
    if (!clip) return;
    const newAction = mixer.clipAction(clip);
    newAction.reset();
    newAction.setLoop(THREE.LoopRepeat, Infinity);
    newAction.clampWhenFinished = false;
    const prev = activeActionRef.current;
    if (prev && prev !== newAction) {
      newAction.crossFadeFrom(prev, 0.4, false);
    }
    newAction.play();
    activeActionRef.current = newAction;
    setActiveClipName(name);
  }, []);

  const toggleExpression = useCallback((name: ExpressionName) => {
    const next = !expressionToggleRef.current[name];
    expressionToggleRef.current = { ...expressionToggleRef.current, [name]: next };
    setActiveExpressions({ ...expressionToggleRef.current });
  }, []);

  const resetCamera = useCallback(() => {
    if (!currentSceneObjectRef.current || !cameraRef.current || !controlsRef.current) return;
    const bbox = new THREE.Box3().setFromObject(currentSceneObjectRef.current);
    const center = bbox.getCenter(new THREE.Vector3());
    const size = bbox.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const fovRad = (cameraRef.current.fov * Math.PI) / 180;
    const dist = (maxDim / 2) / Math.tan(fovRad / 2) * 1.8;
    cameraRef.current.position.set(center.x, center.y + size.y * 0.05, center.z + dist);
    controlsRef.current.target.set(center.x, center.y, center.z);
    controlsRef.current.update();
  }, []);

  const warmUpTTS = useCallback(async () => {
    if (ttsRef.current || ttsState === "warming") return;
    setTtsState("warming");
    setTtsError(null);
    try {
      const tts = new HeadTTS({
        endpoints: ["webgpu", "wasm"],
        languages: ["en-us"],
        voices: ["af_bella"],
        defaultVoice: "af_bella",
        defaultLanguage: "en-us",
        workerModule: HEADTTS_WORKER,
        dictionaryURL: HEADTTS_DICT,
      });
      await tts.connect();
      tts.onmessage = () => { /* swallow */ };
      await tts.synthesize({ input: "Ready." });
      ttsRef.current = tts;
      setTtsState("ready");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setTtsError(msg);
      setTtsState("error");
    }
  }, [ttsState]);

  const speak = useCallback(async (text: string) => {
    const tts = ttsRef.current;
    if (!tts) return;
    setTtsState("speaking");
    const audioCtx = (tts as unknown as { settings: { audioCtx: AudioContext } })
      .settings.audioCtx;
    if (audioCtx.state === "suspended") await audioCtx.resume();
    let nextStartTime = audioCtx.currentTime;
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.4;
    const ampBuf = new Uint8Array(analyser.frequencyBinCount);
    let polling = true;
    const pollAmp = () => {
      if (!polling) return;
      analyser.getByteFrequencyData(ampBuf);
      let sum = 0;
      for (let i = 0; i < ampBuf.length; i++) sum += ampBuf[i];
      const avg = sum / ampBuf.length / 255;
      mouthAmpRef.current = Math.min(1, avg * 2.4);
      requestAnimationFrame(pollAmp);
    };
    pollAmp();
    tts.onmessage = (msg: { data?: { audio?: AudioBuffer } } & Record<string, unknown>) => {
      const d = msg.data;
      if (!d?.audio || typeof (d.audio as AudioBuffer).duration !== "number") return;
      const buf = d.audio;
      const src = audioCtx.createBufferSource();
      src.buffer = buf;
      src.connect(analyser);
      src.connect(audioCtx.destination);
      const startAt = Math.max(audioCtx.currentTime + 0.02, nextStartTime);
      src.start(startAt);
      nextStartTime = startAt + buf.duration;
    };
    try {
      await tts.synthesize({ input: text, voice: "af_bella" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setTtsError(msg);
    } finally {
      window.setTimeout(() => {
        polling = false;
        setTtsState("ready");
      }, text.length * 70 + 1500);
    }
  }, []);

  const hasMorphs = activeCharacter.hasMorphs;

  return (
    <div className="rounded-2xl border bg-white dark:bg-stone-900 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b">
        <div>
          <h2 className="text-base font-semibold">three.js character lab (Kokoro speech)</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            10 CC0 characters · 3 dogs · drag/scroll/pan · per-character animations · lipsync for Robot
          </p>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-wide rounded bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 px-2 py-1">
          CC0 · POLISHED
        </span>
      </div>

      <div className="relative w-full h-[520px] bg-stone-900">
        <div ref={containerRef} className="absolute inset-0" />
        {loadState !== "loaded" ? (
          <div className="absolute inset-0 flex items-center justify-center bg-stone-900/80 backdrop-blur-sm">
            <div className="text-center">
              {loadState === "error" ? (
                <p className="text-xs text-amber-300 font-mono">{loadError}</p>
              ) : (
                <>
                  <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-stone-400" />
                  <p className="text-sm text-stone-300">
                    Loading {activeCharacter.label} {loadPct ? `${loadPct}%` : "…"}
                  </p>
                </>
              )}
            </div>
          </div>
        ) : (
          <button
            onClick={resetCamera}
            className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded bg-stone-800/80 text-stone-200 hover:bg-stone-700 text-[11px]"
            title="Reset camera"
          >
            <RotateCcw className="w-3 h-3" />
            Reset view
          </button>
        )}
      </div>

      <div className="px-5 py-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground mr-1">Character:</span>
          {CHARACTERS.map((c) => (
            <button
              key={c.id}
              onClick={() => switchCharacter(c.id)}
              disabled={loadState === "loading" && c.id !== activeCharacterId}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border transition-colors disabled:opacity-50 ${
                activeCharacterId === c.id
                  ? "bg-orange-600 text-white border-orange-600"
                  : "bg-white text-stone-700 border-stone-300 hover:bg-stone-100"
              }`}
            >
              <span>{c.emoji}</span>
              {c.label}
            </button>
          ))}
        </div>

        {hasMorphs ? (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {ttsState === "cold" ? (
              <button onClick={warmUpTTS} disabled={loadState !== "loaded"}
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600 text-white hover:bg-amber-700 text-sm font-medium disabled:opacity-50">
                <Volume2 className="w-4 h-4" /> Wake up Kokoro (~80 MB)
              </button>
            ) : ttsState === "warming" ? (
              <button disabled className="flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600/60 text-white text-sm font-medium cursor-not-allowed">
                <Loader2 className="w-4 h-4 animate-spin" /> Warming Kokoro…
              </button>
            ) : ttsState === "error" ? (
              <span className="text-xs text-amber-300 font-mono">Kokoro: {ttsError}</span>
            ) : (
              <button onClick={() => speak(TEST_SCRIPT)} disabled={ttsState === "speaking"}
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-orange-700 text-white hover:bg-orange-800 text-sm font-medium disabled:opacity-60">
                {ttsState === "speaking" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
                Speak with Kokoro
              </button>
            )}
          </div>
        ) : null}

        {availableClipNames.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground mr-1">Animation:</span>
            {availableClipNames.map((name) => (
              <button
                key={name}
                onClick={() => playClip(name)}
                disabled={loadState !== "loaded"}
                className={`px-3 py-1.5 rounded-md text-xs border transition-colors disabled:opacity-50 ${
                  activeClipName === name
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-stone-700 border-stone-300 hover:bg-stone-100"
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        ) : null}

        {hasMorphs ? (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground mr-1">Face:</span>
            {EXPRESSIONS.map((e) => (
              <button
                key={e.name}
                onClick={() => toggleExpression(e.name)}
                disabled={loadState !== "loaded"}
                className={`px-3 py-1.5 rounded-md text-xs border transition-colors disabled:opacity-50 ${
                  activeExpressions[e.name]
                    ? "bg-stone-700 text-white border-stone-700"
                    : "bg-white text-stone-700 border-stone-300 hover:bg-stone-100"
                }`}
              >
                {e.name}
              </button>
            ))}
          </div>
        ) : null}

        <p className="text-xs text-muted-foreground pt-1 leading-relaxed">
          Drag to rotate · scroll to zoom · right-drag to pan · Reset view top-right.
          10 CC0 characters: Robot (with lipsync), Soldier, Horse, Husky, Shiba Inu, Wolf, Fox, Flamingo, Parrot, Stork.
          Animals from Quaternius (CC0).
        </p>
      </div>
    </div>
  );
}
